import { assistantMutationTypes } from './types'
import { ASSISTANT_MESSAGE_ROLES } from '@/consts/assistant'
import createState from './state'

/**
 * A reply is an object carrying the same sentence twice — plain in `text`, and
 * with every record it names marked in `text_marked` — plus the `entities` those
 * marks point at. A bare string is the shape the endpoint used to return, kept
 * readable here so a mid-deploy answer is a sentence rather than `[object
 * Object]`.
 */
const readReply = (reply) =>
    typeof reply === 'string'
        ? { content: reply, marked: '', entities: null, blocks: null }
        : {
              content: reply.text || '',
              marked: reply['text_marked'] || '',
              entities: reply.entities || null,
              // The backend saying which part of the answer is a list, and so
              // which records are drawn as cards rather than named in passing.
              blocks: reply.blocks || null,
          }

const mutations = {
    [assistantMutationTypes.SET_TURN](state, turn) {
        state.conversationId = turn['conversation_id']
        state.turnState = turn.state
        state.pending = turn.pending || null
        state.clientCandidates = turn['client_candidates'] || []

        // Once the conversation owns a card the backend returns it on every
        // turn, so a null here means there is none yet, not that one was lost.
        state.wig = turn.wig || null
    },

    /**
     * Put the finished turn into the chat.
     *
     * `replies` carries every line the assistant said, in order — a turn that
     * asks about one thing and creates the card for the rest says two. Only
     * `replies` is read: its last element *is* `reply`, and rendering both
     * would double the final bubble.
     */
    [assistantMutationTypes.APPEND_TURN](state, { sent, turn }) {
        const said = [
            {
                role: ASSISTANT_MESSAGE_ROLES.USER,
                // A dictated turn has no text of its own until the recogniser
                // has been through it.
                content: sent.text || turn.transcript || '',
                isDictated: !!sent.audio,
            },
            ...(turn.replies || [turn.reply])
                .filter(Boolean)
                .map((reply) => ({
                    role: ASSISTANT_MESSAGE_ROLES.ASSISTANT,
                    ...readReply(reply),
                    isDictated: false,
                })),
        ]

        state.messages = [...state.messages, ...said]
    },
    [assistantMutationTypes.SET_OUTGOING](state, outgoing) {
        state.outgoing = outgoing
    },
    [assistantMutationTypes.SET_SENDING](state, isSending) {
        state.isSending = isSending
    },
    [assistantMutationTypes.SET_FAILURE](state, failure) {
        state.failure = failure
    },
    [assistantMutationTypes.SET_SWITCHED_OFF](state, isSwitchedOff) {
        state.isSwitchedOff = isSwitchedOff
    },
    [assistantMutationTypes.SET_CLIENT_ID](state, clientId) {
        state.clientId = clientId || null
    },
    [assistantMutationTypes.CLEAR_CLIENT_CANDIDATES](state) {
        state.clientCandidates = []
    },
    [assistantMutationTypes.SET_LAST_TURN](state, lastTurn) {
        state.lastTurn = lastTurn
    },
    [assistantMutationTypes.RESET](state) {
        // Two facts about this page load rather than about this conversation.
        const { isSwitchedOff, clientId } = state

        Object.assign(state, createState(), { isSwitchedOff, clientId })
    },
}

export default mutations
